import { useMutation, useQuery } from '@apollo/client';

import {
    DELETE_BUSINESS_LOCATION_MUTATION,
    INVITE_NEW_BUSINESS,
    SAVE_BUSINESS_DETAILS_MUTATION,
    SAVE_BUSINESS_OWNER_INFO_MUTATION,
    UPDATE_BUSINESS_STATUS_MUTATION,
    UPLOAD_BUSINESS_LOGO_MUTATION,
    UPLOAD_BUSINESS_USER_PICTURE_MUTATION
} from 'grapqhl/business/mutations';
import {
    BUSINESS_SUBSCRIPTION_DETAIL_QUERY,
    BUSINESS_SUBSCRIPTION_HISTORY_QUERY,
    EXPORT_BUSINESSES_QUERY,
    GET_BUSINESS_DETAILS_QUERY,
    GET_DASHBOARD_BUSINESS_COUNT_QUERY,
    GET_DASHBOARD_REVENUE_COUNT_QUERY
} from 'grapqhl/business/queries';
import { LIST_DATA_POINTS_QUERY } from 'grapqhl/dataPoint/queries';
import { GET_FILE_UPLOAD_URL_QUERY, GET_SIGNED_URL_QUERY } from 'grapqhl/fileUpload/queries';
import {
    CHANGE_STATUS_PROMO_CODE_MUTATION,
    CREATE_PROMO_CODE_MUTATION,
    DELETE_PROMO_CODE_MUTATION,
    UPDATE_PROMO_CODE_MUTATION
} from 'grapqhl/promoCode/mutation';
import { LIST_BRANDED_APP_PROMO_CODES_QUERY, GET_BRANDED_APP_PROMO_CODE_DETAIL_QUERY } from 'grapqhl/brandedAppPromoCode/queries';
import {
    CREATE_BRANDED_APP_PROMO_CODE_MUTATION,
    UPDATE_BRANDED_APP_PROMO_CODE_MUTATION,
    CHANGE_BRANDED_APP_PROMO_CODE_STATUS_MUTATION,
    DELETE_BRANDED_APP_PROMO_CODE_MUTATION
} from 'grapqhl/brandedAppPromoCode/mutations';
import { LIST_OUTSTANDING_INVOICES_QUERY, PAYMENT_LIST_QUERY, REVENUE_BY_SUBSCRIPTION_QUERY } from 'grapqhl/report/queries';
import { ADD_SURVEY_RESPONSE_MUTATION } from 'grapqhl/survey/mutations';
import { LIST_ALL_NPS_SURVEYS_QUERY } from 'grapqhl/survey/queries';
import {
    GET_ONBOARDING_QUERY,
    GET_ONBOARDING_FORM_BY_TOKEN_QUERY,
    GET_CHECKLIST_BY_TOKEN_QUERY,
    GET_CHECKLIST_COMMENTS_QUERY,
    LIST_ONBOARDING_QUERY,
    LIST_ONBOARDING_TEMPLATES_QUERY,
    GET_ONBOARDING_TEMPLATE_QUERY,
    GET_ONBOARDING_QUESTIONNAIRE_QUERY
} from 'grapqhl/onboarding/queries';
import {
    SEND_ONBOARDING_FORM_MUTATION,
    REGENERATE_ONBOARDING_MANUAL_MUTATION,
    UPDATE_ONBOARDING_MANUAL_MUTATION,
    SEND_ONBOARDING_MANUAL_MUTATION,
    SUBMIT_ONBOARDING_FORM_MUTATION,
    SET_CHECKLIST_ITEM_DONE_MUTATION,
    ADD_CHECKLIST_COMMENT_MUTATION,
    REISSUE_ONBOARDING_FORM_LINK_MUTATION,
    REISSUE_ONBOARDING_CHECKLIST_LINK_MUTATION,
    STAFF_ADD_CHECKLIST_COMMENT_MUTATION,
    DELETE_CHECKLIST_COMMENT_MUTATION,
    UPDATE_ONBOARDING_TEMPLATE_MUTATION,
    PUBLISH_ONBOARDING_TEMPLATE_MUTATION,
    UPDATE_ONBOARDING_QUESTIONNAIRE_MUTATION,
    PUBLISH_ONBOARDING_QUESTIONNAIRE_MUTATION
} from 'grapqhl/onboarding/mutations';
import {
    GET_TRAINING_TEMPLATE_QUERY,
    GET_TRAINING_FOR_BUSINESS_QUERY,
    GET_TRAINING_RECORD_QUERY,
    GET_TRAINING_BY_TOKEN_QUERY
} from 'grapqhl/training/queries';
import {
    UPDATE_TRAINING_TEMPLATE_MUTATION,
    PUBLISH_TRAINING_TEMPLATE_MUTATION,
    SEND_TRAINING_MANUAL_MUTATION,
    REISSUE_TRAINING_LINK_MUTATION,
    REVOKE_TRAINING_LINK_MUTATION,
    STAFF_ADD_TRAINING_COMMENT_MUTATION,
    DELETE_TRAINING_COMMENT_MUTATION,
    SET_TRAINING_MODULE_STATUS_MUTATION,
    ADD_TRAINING_COMMENT_MUTATION
} from 'grapqhl/training/mutations';
import {
    GET_ADMIN as ADMIN,
    DELETE_ADMIN as ADMIN_DELETE,
    UPDATE_ADMIN as ADMIN_UPDATE,
    LIST_ADMIN_NOTES as ADMIN_NOTES_LIST,
    ADD_ADMIN_NOTE as ADMIN_NOTE_ADD,
    GET_ADMIN_SIGN_IN_LOG as ADMIN_SIGN_IN_LOG,
    GET_ADMIN_PERMISSION_CHANGE_LOG as ADMIN_PERMISSION_CHANGE_LOG,
    GET_BUSINESS_LOGIN_LOG_FOR_ADMIN as BUSINESS_LOGIN_LOG_FOR_ADMIN,
    GET_TEAM_MEMBER_GOOGLE_CONNECTION as TEAM_MEMBER_GOOGLE_CONNECTION,
    SET_ADMIN_ACTIVE as ADMIN_SET_ACTIVE,
    RESEND_ADMIN_INVITE as ADMIN_RESEND_INVITE,
    SET_ADMIN_CAN_LOGIN as ADMIN_SET_CAN_LOGIN,
    APP_USER_RESET_PASSWORD,
    CHANGE_PASSWORD,
    CODE_VERIFICATION,
    CREATE_MENU as CREATE_MENUS,
    CREATE_PAGE as CREATE_PG,
    CREATE_TAXONOMY as CREATE_TAXONOMYS,
    DELETE_PAGE as DELETE_PG,
    FORGOT_PASSWORD,
    GET_ADMIN_LIST,
    LIST_MENU as GET_LIST_MENU,
    LIST_MENUS as GET_LIST_MENUS,
    LIST_PAGE as GET_LIST_PAGE,
    LIST_PAGES as GET_LIST_PAGES,
    LIST_TAXONOMY as GET_LIST_TAXONOMY,
    GET_PROMO_CODE_DETAIL_QUERY,
    LIST_TAXONOMIES as GET_TAXONOMIES_LIST,
    GET_USER_PROFILE,
    UPLOAD_PROFILE_IMAGE as IMAGE_UPLOAD,
    UPLOAD_MENU_IMAGE as IMG_UPLOAD,
    LIST_ALL_INACTIVE_MEMBERSHIPS_QUERY,
    LIST_ALL_PAID_MEMBERSHIPS_QUERY,
    LIST_ALL_PROMO_CODES_QUERY,
    LIST_ALL_TRIAL_MEMBERSHIPS_QUERY,
    LOGIN,
    DELETE_MENU as MENU_DELETE,
    UPDATE_MENU_STATUS as MENU_STATUS_UPDATE,
    UPDATE_MENU as MENU_UPDATE,
    REGISTER,
    RESEND_OTP_CODE,
    RESET_PASSWORD,
    SEND_RESET_PASSWORD_MAIL,
    DELETE_TAXONOMY as TAXONOMY_DELETE,
    UPDATE_TAXONOMY as TAXONOMY_UPDATE,
    UPDATE_PAGE as UPDATE_PG,
    VALIDATE_OTP,
    VERIFY_TOKEN
} from '../grapqhl';

type GetAdminListDTO = {
    searchText?: string;
    orderBy?: string;
    order?: string;
    limit?: number;
    skip?: number;
};

type GetPagesDTO = {
    input: {
        pageMeta: {
            limit?: number;
            skip?: number;
        };
        searchText?: string;
    };
};

type ListPageDTO = {
    listPage: {
        _id: string;
    };
};

type GetMenusDTO = {
    searchText?: string;
    orderBy?: string;
    order?: string;
    limit?: number;
    skip?: number;
};

type GetListDTO = {
    searchText?: string;
    orderBy?: string;
    order?: string;
    limit?: number;
    skip?: number;
};

type GetTaxonsDTO = {
    searchText?: string;
    orderBy?: string;
    order?: string;
    limit?: number;
    skip?: number;
    taxonomyId: string | undefined;
};

type GetTaxonByTaxonomyIdDTO = {
    taxonomyId: string | undefined;
};

const useGQL = () => {
    const ADMIN_LOGIN = () => useMutation(LOGIN);
    const ADMIN_REGISTER = () => useMutation(REGISTER);
    const FORGOT_PASS = () => useMutation(FORGOT_PASSWORD);
    const RESET_PASS = () => useMutation(RESET_PASSWORD);
    const TOKEN_VERIFY = () => useMutation(VERIFY_TOKEN);
    const SEND_PASSWORD_RESET_MAIL = () => useMutation(SEND_RESET_PASSWORD_MAIL);
    const APP_USER_RESET_PASS = () => useMutation(APP_USER_RESET_PASSWORD);
    const ADMIN_CHANGE_PASSWORD = () => useMutation(CHANGE_PASSWORD);
    const CODE_VERIFY = () => useMutation(CODE_VERIFICATION);
    const RESEND_CODE = () => useMutation(RESEND_OTP_CODE);

    /* admins */
    const GET_ALL_ADMINS = (input: GetAdminListDTO = {}) => useQuery(GET_ADMIN_LIST, { variables: { input } });
    const GET_ADMIN = (id: String, skip: boolean = false) =>
        useQuery(ADMIN, { variables: { id }, skip: skip || !id, fetchPolicy: 'network-only' });
    const UPDATE_ADMIN = () => useMutation(ADMIN_UPDATE);
    const DELETE_ADMIN = () => useMutation(ADMIN_DELETE);
    const SET_ADMIN_ACTIVE = () => useMutation(ADMIN_SET_ACTIVE);
    const RESEND_ADMIN_INVITE = () => useMutation(ADMIN_RESEND_INVITE);
    const LIST_ADMIN_NOTES = (input: { adminId: string; skip?: number; limit?: number; search?: string }) =>
        useQuery(ADMIN_NOTES_LIST, { variables: { input }, fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true });
    const ADD_ADMIN_NOTE = () => useMutation(ADMIN_NOTE_ADD);
    const GET_ADMIN_SIGN_IN_LOG = (input: { adminId: string; skip?: number; limit?: number }) =>
        useQuery(ADMIN_SIGN_IN_LOG, { variables: { input }, fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true });
    const SET_ADMIN_CAN_LOGIN = () => useMutation(ADMIN_SET_CAN_LOGIN);
    const GET_ADMIN_PERMISSION_CHANGE_LOG = (input: { adminId: string; skip?: number; limit?: number }) =>
        useQuery(ADMIN_PERMISSION_CHANGE_LOG, { variables: { input }, fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true });
    const GET_BUSINESS_LOGIN_LOG_FOR_ADMIN = (input: { adminId: string; skip?: number; limit?: number }) =>
        useQuery(BUSINESS_LOGIN_LOG_FOR_ADMIN, { variables: { input }, fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true });
    const GET_TEAM_MEMBER_GOOGLE_CONNECTION = (adminId: string) =>
        useQuery(TEAM_MEMBER_GOOGLE_CONNECTION, { variables: { adminId }, skip: !adminId });
    // cache-and-network so a freshly-changed capability (e.g. canLoginToBusinesses,
    // POGO-13282) is reflected without a full page reload.
    const GET_ADMIN_PROFILE = () => useQuery(GET_USER_PROFILE, { fetchPolicy: 'cache-and-network' });
    const UPLOAD_PROFILE_IMAGE = () => useMutation(IMAGE_UPLOAD);

    const CREATE_PAGE = () => useMutation(CREATE_PG);
    const UPDATE_PAGE = () => useMutation(UPDATE_PG);
    const DELETE_PAGE = () => useMutation(DELETE_PG);
    const LIST_PAGES = (input: GetPagesDTO) =>
        useQuery(GET_LIST_PAGES, { variables: input, notifyOnNetworkStatusChange: true, fetchPolicy: 'network-only' });
    const LIST_PAGE = (input: ListPageDTO) => useQuery(GET_LIST_PAGE, { variables: input, fetchPolicy: 'network-only' });

    const CREATE_MENU = () => useMutation(CREATE_MENUS);
    const LIST_MENUS = (input: GetMenusDTO = {}) => useQuery(GET_LIST_MENUS, { variables: { input }, notifyOnNetworkStatusChange: true });
    const DELETE_MENU = () => useMutation(MENU_DELETE);
    const LIST_MENU = (id: String | undefined) => useQuery(GET_LIST_MENU, { variables: { id }, fetchPolicy: 'network-only' });
    const UPDATE_MENU = () => useMutation(MENU_UPDATE);
    const UPDATE_MENU_STATUS = () => useMutation(MENU_STATUS_UPDATE);
    const UPLOAD_MENU_IMAGE = () => useMutation(IMG_UPLOAD);

    const CREATE_TAXONOMY = () => useMutation(CREATE_TAXONOMYS);
    const UPDATE_TAXONOMY = () => useMutation(TAXONOMY_UPDATE);
    const DELETE_TAXONOMY = () => useMutation(TAXONOMY_DELETE);
    const LIST_TAXONOMY = (id: String | undefined) => useQuery(GET_LIST_TAXONOMY, { variables: { id }, fetchPolicy: 'network-only' });
    const LIST_TAXONOMIES = (input: GetListDTO = {}) =>
        useQuery(GET_TAXONOMIES_LIST, { variables: { input }, notifyOnNetworkStatusChange: true });
    const VALIDATE_AUTH_OTP = () => useMutation(VALIDATE_OTP);

    const GET_SIGNED_URL = (input: { sizes: Array<string>; key: string }, skip = false, onCompleted?: (data: any) => void) =>
        useQuery(GET_SIGNED_URL_QUERY, { variables: { input }, skip, ...(onCompleted ? { onCompleted } : {}) });

    const GET_FILE_UPLOAD_URL = (input: { key: string }, skip = false) =>
        useQuery(GET_FILE_UPLOAD_URL_QUERY, { variables: { input }, skip });

    const LIST_DATA_POINTS = (input: any, skip?: boolean) => useQuery(LIST_DATA_POINTS_QUERY, { variables: input, skip });
    const LIST_PROMO_CODES = (input: any = {}) => useQuery(LIST_ALL_PROMO_CODES_QUERY, { variables: input });
    const CREATE_PROMO_CODE = () => useMutation(CREATE_PROMO_CODE_MUTATION);
    const CHANGE_STATUS_PROMO_CODE = () => useMutation(CHANGE_STATUS_PROMO_CODE_MUTATION);
    const UPDATE_PROMO_CODE = () => useMutation(UPDATE_PROMO_CODE_MUTATION);
    const GET_PROMO_CODE_DETAIL = (promoCodeId: String, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(GET_PROMO_CODE_DETAIL_QUERY, {
            variables: { getPromoCodeDetails: { promoCodeId: promoCodeId } },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });
    const DELETE_PROMO_CODE = () => useMutation(DELETE_PROMO_CODE_MUTATION);

    /* branded app promo codes */
    const LIST_BRANDED_APP_PROMO_CODES = () => useQuery(LIST_BRANDED_APP_PROMO_CODES_QUERY, { fetchPolicy: 'network-only' });
    const GET_BRANDED_APP_PROMO_CODE_DETAIL = (promoCodeId: string, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(GET_BRANDED_APP_PROMO_CODE_DETAIL_QUERY, {
            variables: { input: { promoCodeId } },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });
    const CREATE_BRANDED_APP_PROMO_CODE = () => useMutation(CREATE_BRANDED_APP_PROMO_CODE_MUTATION);
    const UPDATE_BRANDED_APP_PROMO_CODE = () => useMutation(UPDATE_BRANDED_APP_PROMO_CODE_MUTATION);
    const CHANGE_BRANDED_APP_PROMO_CODE_STATUS = () => useMutation(CHANGE_BRANDED_APP_PROMO_CODE_STATUS_MUTATION);
    const DELETE_BRANDED_APP_PROMO_CODE = () => useMutation(DELETE_BRANDED_APP_PROMO_CODE_MUTATION);

    const EXPORT_BUSINESSES = () => useQuery(EXPORT_BUSINESSES_QUERY);
    const UPDATE_BUSINESSS_STATUS = () => useMutation(UPDATE_BUSINESS_STATUS_MUTATION);
    const UPLOAD_BUSINESSS_LOGO = () => useMutation(UPLOAD_BUSINESS_LOGO_MUTATION);
    const SAVE_BUSINESSS_DETAILS = () => useMutation(SAVE_BUSINESS_DETAILS_MUTATION);
    const DELETE_BUSINESSS_LOCATION = () => useMutation(DELETE_BUSINESS_LOCATION_MUTATION);
    const UPLOAD_BUSINESS_USER_PICTURE = () => useMutation(UPLOAD_BUSINESS_USER_PICTURE_MUTATION);
    const SAVE_BUSINESS_OWNER_INFO = () => useMutation(SAVE_BUSINESS_OWNER_INFO_MUTATION);

    const GET_BUSINESS_DETAILS = (input: any, onCompleted?: (data: any) => void) =>
        useQuery(GET_BUSINESS_DETAILS_QUERY, { variables: input, ...(onCompleted ? { onCompleted } : {}) });

    const INVITE_BUSINESS = () => useMutation(INVITE_NEW_BUSINESS);

    const ADD_SURVEY_RESPONSE = () => useMutation(ADD_SURVEY_RESPONSE_MUTATION);
    const LIST_ALL_NPS_SURVEYS = (input, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(LIST_ALL_NPS_SURVEYS_QUERY, {
            variables: { listNpsSurveys: input },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });

    const LIST_ALL_PAID_MEMBERSHIPS = (input, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(LIST_ALL_PAID_MEMBERSHIPS_QUERY, {
            variables: { listPaidMembership: input },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });
    const LIST_ALL_INACTIVE_MEMBERSHIPS = (input, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(LIST_ALL_INACTIVE_MEMBERSHIPS_QUERY, {
            variables: { listCancelledMembership: input },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });
    const LIST_ALL_TRIAL_MEMBERSHIPS = (input, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(LIST_ALL_TRIAL_MEMBERSHIPS_QUERY, {
            variables: { listTrialMembership: input },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });

    const GET_DASHBOARD_BUSINESS_COUNT = () => useQuery(GET_DASHBOARD_BUSINESS_COUNT_QUERY);
    const GET_DASHBOARD_REVENUE_COUNT = () => useQuery(GET_DASHBOARD_REVENUE_COUNT_QUERY);

    const LIST_ALL_OUTSTANDING_INVOICES = (input, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(LIST_OUTSTANDING_INVOICES_QUERY, {
            variables: { input: input },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });

    const LIST_ALL_PAYMENT = (input, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(PAYMENT_LIST_QUERY, {
            variables: { input: input },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });

    const GET_REVENUE_MEMBERSHIP_REPORT = (input: any) =>
        useQuery(REVENUE_BY_SUBSCRIPTION_QUERY, {
            variables: { input }
        });

    const BUSINESS_SUBSCRIPTION_DETAIL = (input) =>
        useQuery(BUSINESS_SUBSCRIPTION_DETAIL_QUERY, { variables: { businessSubscriptionDetail: input } });

    const BUSINESS_SUBSCRIPTION_HISTORY = (input, skip: boolean = false, onCompleted?: (data: any) => void) =>
        useQuery(BUSINESS_SUBSCRIPTION_HISTORY_QUERY, {
            variables: { businessSubscriptionHistory: input },
            skip,
            ...(onCompleted ? { onCompleted } : {})
        });

    const GET_ONBOARDING = (businessId: string | undefined, skip: boolean = false) =>
        useQuery(GET_ONBOARDING_QUERY, { variables: { businessId }, skip: skip || !businessId, fetchPolicy: 'network-only' });
    const SEND_ONBOARDING_FORM = () => useMutation(SEND_ONBOARDING_FORM_MUTATION);
    const REGENERATE_ONBOARDING_MANUAL = () => useMutation(REGENERATE_ONBOARDING_MANUAL_MUTATION);
    const UPDATE_ONBOARDING_MANUAL = () => useMutation(UPDATE_ONBOARDING_MANUAL_MUTATION);
    const SEND_ONBOARDING_MANUAL = () => useMutation(SEND_ONBOARDING_MANUAL_MUTATION);
    const REISSUE_ONBOARDING_FORM_LINK = () => useMutation(REISSUE_ONBOARDING_FORM_LINK_MUTATION);
    const REISSUE_ONBOARDING_CHECKLIST_LINK = () => useMutation(REISSUE_ONBOARDING_CHECKLIST_LINK_MUTATION);
    const STAFF_ADD_CHECKLIST_COMMENT = () => useMutation(STAFF_ADD_CHECKLIST_COMMENT_MUTATION);
    const DELETE_CHECKLIST_COMMENT = () => useMutation(DELETE_CHECKLIST_COMMENT_MUTATION);
    const LIST_ONBOARDING_TEMPLATES = () => useQuery(LIST_ONBOARDING_TEMPLATES_QUERY, { fetchPolicy: 'network-only' });
    const GET_ONBOARDING_TEMPLATE = (variant: string | undefined) =>
        useQuery(GET_ONBOARDING_TEMPLATE_QUERY, { variables: { variant }, skip: !variant, fetchPolicy: 'network-only' });
    const UPDATE_ONBOARDING_TEMPLATE = () => useMutation(UPDATE_ONBOARDING_TEMPLATE_MUTATION);
    const PUBLISH_ONBOARDING_TEMPLATE = () => useMutation(PUBLISH_ONBOARDING_TEMPLATE_MUTATION);
    const GET_ONBOARDING_QUESTIONNAIRE = () => useQuery(GET_ONBOARDING_QUESTIONNAIRE_QUERY, { fetchPolicy: 'network-only' });
    const UPDATE_ONBOARDING_QUESTIONNAIRE = () => useMutation(UPDATE_ONBOARDING_QUESTIONNAIRE_MUTATION);
    const PUBLISH_ONBOARDING_QUESTIONNAIRE = () => useMutation(PUBLISH_ONBOARDING_QUESTIONNAIRE_MUTATION);
    const LIST_ONBOARDING = (skip = 0, limit = 50) =>
        useQuery(LIST_ONBOARDING_QUERY, { variables: { skip, limit }, fetchPolicy: 'network-only' });

    // Public (unauthenticated) — the form token in the URL is the authorization.
    const GET_ONBOARDING_FORM_BY_TOKEN = (formToken: string | undefined) =>
        useQuery(GET_ONBOARDING_FORM_BY_TOKEN_QUERY, {
            variables: { formToken },
            skip: !formToken,
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        });
    const SUBMIT_ONBOARDING_FORM = () => useMutation(SUBMIT_ONBOARDING_FORM_MUTATION);
    const GET_CHECKLIST_BY_TOKEN = (checklistToken: string | undefined) =>
        useQuery(GET_CHECKLIST_BY_TOKEN_QUERY, {
            variables: { checklistToken },
            skip: !checklistToken,
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        });
    const SET_CHECKLIST_ITEM_DONE = () => useMutation(SET_CHECKLIST_ITEM_DONE_MUTATION);
    const ADD_CHECKLIST_COMMENT = () => useMutation(ADD_CHECKLIST_COMMENT_MUTATION);
    const GET_CHECKLIST_COMMENTS = (manualId: string | undefined) =>
        useQuery(GET_CHECKLIST_COMMENTS_QUERY, { variables: { manualId }, skip: !manualId, fetchPolicy: 'network-only' });

    /* training manuals (staff) */
    const GET_TRAINING_TEMPLATE = () => useQuery(GET_TRAINING_TEMPLATE_QUERY, { fetchPolicy: 'network-only' });
    const UPDATE_TRAINING_TEMPLATE = () => useMutation(UPDATE_TRAINING_TEMPLATE_MUTATION);
    const PUBLISH_TRAINING_TEMPLATE = () => useMutation(PUBLISH_TRAINING_TEMPLATE_MUTATION);
    const GET_TRAINING_FOR_BUSINESS = (businessId: string | undefined, skip: boolean = false) =>
        useQuery(GET_TRAINING_FOR_BUSINESS_QUERY, { variables: { businessId }, skip: skip || !businessId, fetchPolicy: 'network-only' });
    const GET_TRAINING_RECORD = (teamMemberId: string | undefined) =>
        useQuery(GET_TRAINING_RECORD_QUERY, { variables: { teamMemberId }, skip: !teamMemberId, fetchPolicy: 'network-only' });
    const SEND_TRAINING_MANUAL = () => useMutation(SEND_TRAINING_MANUAL_MUTATION);
    const REISSUE_TRAINING_LINK = () => useMutation(REISSUE_TRAINING_LINK_MUTATION);
    const REVOKE_TRAINING_LINK = () => useMutation(REVOKE_TRAINING_LINK_MUTATION);
    const STAFF_ADD_TRAINING_COMMENT = () => useMutation(STAFF_ADD_TRAINING_COMMENT_MUTATION);
    const DELETE_TRAINING_COMMENT = () => useMutation(DELETE_TRAINING_COMMENT_MUTATION);

    // Public (unauthenticated) — the training token in the URL is the authorization.
    const GET_TRAINING_BY_TOKEN = (token: string | undefined) =>
        useQuery(GET_TRAINING_BY_TOKEN_QUERY, {
            variables: { token },
            skip: !token,
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        });
    const SET_TRAINING_MODULE_STATUS = () => useMutation(SET_TRAINING_MODULE_STATUS_MUTATION);
    const ADD_TRAINING_COMMENT = () => useMutation(ADD_TRAINING_COMMENT_MUTATION);

    return {
        GET_CHECKLIST_COMMENTS,
        GET_CHECKLIST_BY_TOKEN,
        SET_CHECKLIST_ITEM_DONE,
        ADD_CHECKLIST_COMMENT,
        GET_ONBOARDING_FORM_BY_TOKEN,
        SUBMIT_ONBOARDING_FORM,
        GET_ONBOARDING,
        LIST_ONBOARDING,
        SEND_ONBOARDING_FORM,
        REGENERATE_ONBOARDING_MANUAL,
        UPDATE_ONBOARDING_MANUAL,
        SEND_ONBOARDING_MANUAL,
        REISSUE_ONBOARDING_FORM_LINK,
        REISSUE_ONBOARDING_CHECKLIST_LINK,
        STAFF_ADD_CHECKLIST_COMMENT,
        DELETE_CHECKLIST_COMMENT,
        LIST_ONBOARDING_TEMPLATES,
        GET_ONBOARDING_TEMPLATE,
        UPDATE_ONBOARDING_TEMPLATE,
        PUBLISH_ONBOARDING_TEMPLATE,
        GET_ONBOARDING_QUESTIONNAIRE,
        UPDATE_ONBOARDING_QUESTIONNAIRE,
        PUBLISH_ONBOARDING_QUESTIONNAIRE,
        GET_TRAINING_TEMPLATE,
        UPDATE_TRAINING_TEMPLATE,
        PUBLISH_TRAINING_TEMPLATE,
        GET_TRAINING_FOR_BUSINESS,
        GET_TRAINING_RECORD,
        SEND_TRAINING_MANUAL,
        REISSUE_TRAINING_LINK,
        REVOKE_TRAINING_LINK,
        STAFF_ADD_TRAINING_COMMENT,
        DELETE_TRAINING_COMMENT,
        GET_TRAINING_BY_TOKEN,
        SET_TRAINING_MODULE_STATUS,
        ADD_TRAINING_COMMENT,
        ADMIN_LOGIN,
        ADMIN_REGISTER,
        FORGOT_PASS,
        RESET_PASS,
        TOKEN_VERIFY,
        SEND_PASSWORD_RESET_MAIL,
        APP_USER_RESET_PASS,
        GET_ALL_ADMINS,
        GET_ADMIN,
        UPDATE_ADMIN,
        DELETE_ADMIN,
        SET_ADMIN_ACTIVE,
        RESEND_ADMIN_INVITE,
        LIST_ADMIN_NOTES,
        ADD_ADMIN_NOTE,
        GET_ADMIN_SIGN_IN_LOG,
        SET_ADMIN_CAN_LOGIN,
        GET_ADMIN_PERMISSION_CHANGE_LOG,
        GET_BUSINESS_LOGIN_LOG_FOR_ADMIN,
        GET_TEAM_MEMBER_GOOGLE_CONNECTION,
        GET_ADMIN_PROFILE,
        ADMIN_CHANGE_PASSWORD,
        CREATE_PAGE,
        UPDATE_PAGE,
        DELETE_PAGE,
        LIST_PAGES,
        LIST_PAGE,
        CREATE_MENU,
        LIST_MENUS,
        DELETE_MENU,
        LIST_MENU,
        UPDATE_MENU,
        UPDATE_MENU_STATUS,
        UPLOAD_MENU_IMAGE,
        CREATE_TAXONOMY,
        UPDATE_TAXONOMY,
        DELETE_TAXONOMY,
        LIST_TAXONOMY,
        LIST_TAXONOMIES,
        CODE_VERIFY,
        RESEND_CODE,
        UPLOAD_PROFILE_IMAGE,
        VALIDATE_AUTH_OTP,

        LIST_DATA_POINTS,
        LIST_PROMO_CODES,
        CREATE_PROMO_CODE,
        UPDATE_PROMO_CODE,
        CHANGE_STATUS_PROMO_CODE,
        GET_PROMO_CODE_DETAIL,
        DELETE_PROMO_CODE,

        LIST_BRANDED_APP_PROMO_CODES,
        GET_BRANDED_APP_PROMO_CODE_DETAIL,
        CREATE_BRANDED_APP_PROMO_CODE,
        UPDATE_BRANDED_APP_PROMO_CODE,
        CHANGE_BRANDED_APP_PROMO_CODE_STATUS,
        DELETE_BRANDED_APP_PROMO_CODE,

        EXPORT_BUSINESSES,
        UPDATE_BUSINESSS_STATUS,
        UPLOAD_BUSINESSS_LOGO,
        SAVE_BUSINESSS_DETAILS,
        GET_BUSINESS_DETAILS,
        DELETE_BUSINESSS_LOCATION,
        UPLOAD_BUSINESS_USER_PICTURE,
        SAVE_BUSINESS_OWNER_INFO,
        GET_SIGNED_URL,
        GET_FILE_UPLOAD_URL,
        INVITE_BUSINESS,

        ADD_SURVEY_RESPONSE,
        LIST_ALL_NPS_SURVEYS,

        LIST_ALL_PAID_MEMBERSHIPS,
        LIST_ALL_INACTIVE_MEMBERSHIPS,
        LIST_ALL_TRIAL_MEMBERSHIPS,

        GET_DASHBOARD_BUSINESS_COUNT,
        GET_DASHBOARD_REVENUE_COUNT,

        LIST_ALL_OUTSTANDING_INVOICES,
        LIST_ALL_PAYMENT,
        GET_REVENUE_MEMBERSHIP_REPORT,
        BUSINESS_SUBSCRIPTION_DETAIL,
        BUSINESS_SUBSCRIPTION_HISTORY
    };
};

export default useGQL;
